import { useEffect, useState } from "react";
import { Box, Center, Text } from "@chakra-ui/react";
import { Circle } from "rc-progress";
import { formatDate } from "utils/conversion/formatTime";
import "./InvoiceTimer.css";

function InvoiceTimer({ created, expires }) {
  // rc-progress paints these through `style`, so a CSS variable resolves.
  // The ring is non-text (brand.accent, >= 3:1); the time inside it is
  // normal-size text (brand.solid, 5.42:1 light). The trail is decorative.
  const ringColor = "var(--ips-primary)";
  const timeColor = "var(--ips-primary-strong)";
  const trailColor = "var(--ips-border)";

  const [remainingTime, setRemainingTime] = useState(
    calculateRemainingTime(created, expires)
  );

  function calculateRemainingTime(created, expires) {
    // Check for created and expires
    if (!created || !expires) {
      return {
        timeRemaining: "Pending",
        percentPassed: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }
    
    const now               = new Date();
    const creationTime      = new Date(created);
    const expirationTime    = new Date(expires);
    const totalMilliseconds = Number(expirationTime) - Number(now);
    const timeDifference    = Math.max(0, totalMilliseconds);
    
    if (timeDifference <= 0) {
      return {
        timeRemaining: "Expired",
        percentPassed: 100,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }

    const totalDuration = Number(expirationTime) - Number(creationTime);
    const percentPassed = ((totalDuration - timeDifference) / totalDuration) * 100;
    const seconds       = Math.floor((timeDifference / 1000) % 60);
    const minutes       = Math.floor((timeDifference / (1000 * 60)) % 60);
    const hours         = Math.floor((timeDifference / (1000 * 60 * 60)) % 24);
    const days          = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    let timeRemaining = "";

    if (days > 0) {timeRemaining += `${days}d `;}
    if (hours > 0 || timeRemaining !== "") {timeRemaining += `${hours}h `;}
    if (minutes > 0 || timeRemaining !== "") {timeRemaining += `${minutes}m `;}

    timeRemaining += `${seconds}s`;

    return {
      timeRemaining: timeRemaining.trim(),
      percentPassed: percentPassed,
      hours,
      minutes,
      seconds,
    };
  }

  useEffect(() => {
    // Don't start timer if there's no created or expires
    if (!created || !expires) {
      return;
    }

    const timerInterval = setInterval(() => {
      const remaining = calculateRemainingTime(created, expires);
      setRemainingTime(remaining);
      if (remaining.timeRemaining === "Expired") {
        clearInterval(timerInterval);
      }
    }, 10000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [expires, created]);

  return (
    <>
      {remainingTime.timeRemaining === "Pending" ? (
        <Text textColor="text.secondary">Timer will start after selecting payment method</Text>
      ) : remainingTime.timeRemaining !== "Expired" ? (
        <Box textAlign="center">
          <Text fontWeight="semibold" mb={4}>
            Time until payment deadline
          </Text>

          {/* Progress circle with time in center */}
          <Center className="invoice-timer-progress-container" mb={4}>
            <div className="invoice-timer-progress-percentage">
              <span style={{ color: timeColor }}>{remainingTime.timeRemaining}</span>
            </div>
            <Circle
              percent={+remainingTime.percentPassed.toFixed()}
              strokeWidth={8}
              trailWidth={8}
              strokeColor={ringColor}
              trailColor={trailColor}
              className="invoice-timer-progress-circle"
              strokeLinecap="round"
            />
          </Center>

          <Box mt={2}>
            <Text fontWeight="semibold" mb={1}>
              Dates
            </Text>
            <Text fontSize="sm" color="text.secondary">
              Created: {formatDate(created)}
            </Text>
            <Text fontSize="sm" color="text.secondary">
              Payment Due: {formatDate(expires)}
            </Text>
          </Box>
        </Box>
      ) : (
        <Text textColor="status.warning">Invoice is expired</Text>
      )}
    </>
  );
}

export default InvoiceTimer;
